// Animation effect handlers for different effect types
import * as Three from 'three';
import { getParticleConfiguredOpacity } from '../../../../utils/ThreeJsUtils/materialOpacitySnapshot';
import { setUserDataField } from '../../../../utils/ThreeJsUtils/syncObjectTransform';
import {
  applyScaleEffectToObject,
  applyPulseScaleToObject,
} from '../../../gridpagefunction/Text';

/** Find the child/descendant that owns the GLB mixer (pivot groups wrap the actual model). */
export const getAnimatableObject = (obj) => {
  if (!obj) return null;

  if (
    obj.userData &&
    (obj.userData.mixer ||
      (obj.userData.animationClips && obj.userData.animationClips.length > 0))
  ) {
    return obj;
  }

  let found = null;
  obj.traverse((child) => {
    if (found) return;
    if (
      child !== obj &&
      child.userData &&
      (child.userData.mixer ||
        (child.userData.animationClips &&
          child.userData.animationClips.length > 0))
    ) {
      found = child;
    }
  });

  return found;
};

/** True when the object (or a descendant) has playable GLB animation clips. */
export const objectHasGlbAnimation = (obj) => {
  const animatable = getAnimatableObject(obj);
  if (!animatable) return false;
  const clips = animatable.userData?.animationClips;
  if (Array.isArray(clips) && clips.length > 0) {
    return clips.some(
      (clip) =>
        clip &&
        (typeof clip.duration === 'number' ||
          (Array.isArray(clip.tracks) && clip.tracks.length > 0) ||
          (typeof clip.name === 'string' && clip.name.length > 0))
    );
  }
  const actions = animatable.userData?.animationActions;
  return Array.isArray(actions) && actions.length > 0;
};

/** Resolve a scene asset root by animation panel object id (never nested GLB nodes). */
export const resolveAnimationSceneObject = (scene, objectId) => {
  if (!scene || objectId == null || objectId === '') return null;

  const id = String(objectId);
  const direct = scene.getObjectByName(id);
  if (direct) return direct;

  let found = null;
  scene.traverse((child) => {
    if (found) return;
    const matches =
      String(child.name) === id ||
      String(child._id) === id ||
      String(child.url) === id ||
      String(child.temp_file_name) === id;
    if (!matches) return;
    if (child.parent === scene || child.deleteType) {
      found = child;
    }
  });
  return found;
};

/** Give a duplicated GLB its own mixers/actions (clone() shares mixer references). */
export const isolateGlbAnimationMixers = (root) => {
  if (!root) return;

  root.traverse((node) => {
    const clips = node.userData?.animationClips;
    if (!Array.isArray(clips) || clips.length === 0) return;

    if (Array.isArray(node.userData.animationActions)) {
      node.userData.animationActions.forEach((action) => {
        try {
          action.stop();
          action.reset();
        } catch (_) {
          /* ignore */
        }
      });
    }

    const mixer = new Three.AnimationMixer(node);
    node.userData.mixer = mixer;
    node.userData.animationActions = [];
    clips.forEach((clip) => {
      try {
        const action = mixer.clipAction(clip);
        action.reset();
        action.setLoop(Three.LoopRepeat);
        node.userData.animationActions.push(action);
      } catch (e) {
        console.warn('Failed to create animation action for duplicate', e);
      }
    });
    setUserDataField(node, 'isGlbPlaying', false);
    setUserDataField(node, 'activePlayModelEffect', null);
    setUserDataField(node, 'previousClipActionIndex', -1);
  });

  setUserDataField(root, 'isGlbPlaying', false);
  setUserDataField(root, 'activePlayModelEffect', null);
  setUserDataField(root, 'previousClipActionIndex', -1);
};

/** Reuse one mixer per animatable node — never stack mixers on repeated play clicks. */
export const ensureGlbMixer = (animatableObj) => {
  if (!animatableObj?.userData) return null;

  const clips = animatableObj.userData.animationClips;
  if (!Array.isArray(clips) || clips.length === 0) return null;

  if (animatableObj.userData.mixer) {
    if (
      !Array.isArray(animatableObj.userData.animationActions) ||
      animatableObj.userData.animationActions.length !== clips.length
    ) {
      animatableObj.userData.animationActions = clips.map((clip) => {
        const action = animatableObj.userData.mixer.clipAction(clip);
        action.reset();
        action.setLoop(Three.LoopRepeat);
        return action;
      });
    }
    return animatableObj.userData.mixer;
  }

  const mixer = new Three.AnimationMixer(animatableObj);
  animatableObj.userData.mixer = mixer;
  animatableObj.userData.animationActions = clips.map((clip) => {
    const action = mixer.clipAction(clip);
    action.reset();
    action.setLoop(Three.LoopRepeat);
    return action;
  });
  return mixer;
};

/** Resume GLB clip actions only when playModel is actively driving this asset. */
export const resumeGlbAnimationsForRoot = (sceneRoot, options = {}) => {
  if (!sceneRoot) return;
  const { continuousOnly = true } = options;
  const animatable = getAnimatableObject(sceneRoot) || sceneRoot;
  if (!animatable?.userData?.animationActions?.length) return;

  if (continuousOnly && animatable.userData.activePlayModelEffect == null) {
    return;
  }

  animatable.userData.animationActions.forEach((action) => {
    action.paused = false;
    if (!action.isRunning()) action.play();
  });
  setUserDataField(animatable, 'isGlbPlaying', true);
  setUserDataField(sceneRoot, 'isGlbPlaying', true);
};

export const applyEasing = (progress, easingType) => {
  switch (easingType) {
    case 'Ease-in':
      return progress * progress;
    case 'Ease-out':
      return 1 - Math.pow(1 - progress, 2);
    case 'In-out':
      return progress < 0.5
        ? 2 * progress * progress
        : 1 - Math.pow(-2 * progress + 2, 2) / 2;
    case 'None':
    default:
      return progress;
  }
};

// Enhanced move effect application
export const applyMoveEffect = (
  obj,
  effect,
  easedProgress,
  newTime,
  initialPos
) => {
  const movementType = effect.movementType || 'linear';
  const startPos = effect.startPosition || { x: 0, y: 0, z: 0 };
  const endPos = effect.endPosition || { x: 10, y: 0, z: 0 };
  const activeAxes = effect.activeAxes || { x: true, y: false, z: true };

  switch (movementType) {
    case 'linear': {
      // Original linear movement
      if (activeAxes.x)
        obj.position.x = startPos.x + (endPos.x - startPos.x) * easedProgress;
      if (activeAxes.y)
        obj.position.y = startPos.y + (endPos.y - startPos.y) * easedProgress;
      if (activeAxes.z)
        obj.position.z = startPos.z + (endPos.z - startPos.z) * easedProgress;
      break;
    }

    case 'circular': {
      const center = effect.circleCenter || { x: 0, y: 0, z: 0 };
      const radius = effect.circleRadius || 5;
      const axis = effect.circleAxis || 'z';
      const direction = effect.circleDirection === 'counterclockwise' ? -1 : 1;
      const startAngle = ((effect.circleStartAngle || 0) * Math.PI) / 180;
      const endAngle = ((effect.circleEndAngle || 360) * Math.PI) / 180;
      const revolutions = effect.circleRevolutions || 1;

      const totalAngle =
        endAngle - startAngle + (revolutions - 1) * 2 * Math.PI;
      const currentAngle = startAngle + totalAngle * easedProgress * direction;

      if (axis === 'z') {
        obj.position.x = center.x + radius * Math.cos(currentAngle);
        obj.position.y = center.y + radius * Math.sin(currentAngle);
        obj.position.z = center.z;
      } else if (axis === 'y') {
        obj.position.x = center.x + radius * Math.cos(currentAngle);
        obj.position.z = center.z + radius * Math.sin(currentAngle);
        obj.position.y = center.y;
      } else if (axis === 'x') {
        obj.position.y = center.y + radius * Math.cos(currentAngle);
        obj.position.z = center.z + radius * Math.sin(currentAngle);
        obj.position.x = center.x;
      }
      break;
    }

    case 'bezier': {
      const control1 = effect.bezierControl1 || { x: 5, y: 5, z: 0 };
      const control2 = effect.bezierControl2 || { x: 10, y: -5, z: 0 };

      // Cubic Bezier curve calculation
      const t = easedProgress;
      const t2 = t * t;
      const t3 = t2 * t;
      const mt = 1 - t;
      const mt2 = mt * mt;
      const mt3 = mt2 * mt;

      obj.position.x =
        mt3 * startPos.x +
        3 * mt2 * t * control1.x +
        3 * mt * t2 * control2.x +
        t3 * endPos.x;
      obj.position.y =
        mt3 * startPos.y +
        3 * mt2 * t * control1.y +
        3 * mt * t2 * control2.y +
        t3 * endPos.y;
      obj.position.z =
        mt3 * startPos.z +
        3 * mt2 * t * control1.z +
        3 * mt * t2 * control2.z +
        t3 * endPos.z;
      break;
    }

    case 'sine': {
      const amplitude = effect.sineAmplitude || { x: 2, y: 0, z: 0 };
      const frequency = effect.sineFrequency || 1;
      const phase = ((effect.sinePhase || 0) * Math.PI) / 180;
      const sineAxis = effect.sineAxis || 'x';

      const sineValue = Math.sin(newTime * frequency * 2 * Math.PI + phase);
      const sineOffset = (amplitude[sineAxis] ?? 0) * sineValue;

      obj.position.x = startPos.x + (endPos.x - startPos.x) * easedProgress;
      obj.position.y = startPos.y + (endPos.y - startPos.y) * easedProgress;
      obj.position.z = startPos.z + (endPos.z - startPos.z) * easedProgress;
      obj.position[sineAxis] += sineOffset;
      break;
    }

    case 'figure8': {
      const radius = effect.figure8Radius || 5;
      const axis = effect.figure8Axis || 'z';
      const speed = effect.figure8Speed || 0.5;

      const t = newTime * speed * 2 * Math.PI;
      const x = radius * Math.sin(t);
      const y = (radius * Math.sin(2 * t)) / 2;

      if (axis === 'z') {
        obj.position.x = startPos.x + x;
        obj.position.y = startPos.y + y;
        obj.position.z = startPos.z + (endPos.z - startPos.z) * easedProgress;
      } else if (axis === 'y') {
        obj.position.x = startPos.x + x;
        obj.position.z = startPos.z + y;
        obj.position.y = startPos.y + (endPos.y - startPos.y) * easedProgress;
      } else if (axis === 'x') {
        obj.position.y = startPos.y + x;
        obj.position.z = startPos.z + y;
        obj.position.x = startPos.x + (endPos.x - startPos.x) * easedProgress;
      }
      break;
    }

    case 'spiral': {
      const radius = effect.spiralRadius || 5;
      const height = effect.spiralHeight || 5;
      const turns = effect.spiralTurns || 2;
      const axis = effect.spiralAxis || 'z';
      const direction = effect.spiralDirection === 'down' ? -1 : 1;

      const angle = easedProgress * turns * 2 * Math.PI;
      const currentRadius = radius * (1 - easedProgress);
      const currentHeight = height * easedProgress * direction;

      if (axis === 'z') {
        obj.position.x = startPos.x + currentRadius * Math.cos(angle);
        obj.position.y = startPos.y + currentRadius * Math.sin(angle);
        obj.position.z = startPos.z + currentHeight;
      } else if (axis === 'y') {
        obj.position.x = startPos.x + currentRadius * Math.cos(angle);
        obj.position.z = startPos.z + currentRadius * Math.sin(angle);
        obj.position.y = startPos.y + currentHeight;
      } else if (axis === 'x') {
        obj.position.y = startPos.y + currentRadius * Math.cos(angle);
        obj.position.z = startPos.z + currentRadius * Math.sin(angle);
        obj.position.x = startPos.x + currentHeight;
      }
      break;
    }
  }

  // Handle auto-rotation if enabled
  if (effect.autoRotate) {
    const lookAtTarget = effect.lookAtTarget || { x: 0, y: 0, z: 0 };
    obj.lookAt(lookAtTarget.x, lookAtTarget.y, lookAtTarget.z);
  }
};

// Apply additive effects that modify the base position
export const applyAdditiveAnimationEffect = (
  obj,
  effect,
  easedProgress,
  newTime,
  basePosition
) => {
  switch (effect.effectType) {
    case 'shake': {
      const shakeIntensity = effect.shakeIntensity ?? 0.2;
      const shakeFrequency = effect.shakeFrequency ?? 10;
      const shakeAxes = effect.shakeAxes ?? { x: true, y: true, z: false };
      const shakeDecay = effect.shakeDecay ?? 0.95;
      const shakeType = effect.shakeType ?? 'random';

      const decayFactor = Math.pow(shakeDecay, easedProgress * 10);
      const intensity = shakeIntensity * decayFactor;
      // Interpret shakeFrequency as cycles-per-second (Hz). Convert to radians/sec.
      const time = newTime * shakeFrequency * 2 * Math.PI;

      let shakeX = 0,
        shakeY = 0,
        shakeZ = 0;
      if (shakeType === 'sine') {
        shakeX = Math.sin(time) * intensity;
        shakeY = Math.sin(time + Math.PI / 2) * intensity;
        shakeZ = Math.sin(time + Math.PI) * intensity;
      } else if (shakeType === 'random') {
        // Create deterministic, frequency-driven pseudo-noise so frequency controls
        // how fast the random-like offsets change. Using several sine waves with
        // different multipliers produces a noisier feel while keeping it
        // deterministic and controllable by `shakeFrequency`.
        shakeX = Math.sin(time * 1.37) * intensity;
        shakeY = Math.sin(time * 1.73 + 1.0) * intensity;
        shakeZ = Math.sin(time * 2.11 + 2.0) * intensity;
      }

      // Add shake to base position
      obj.position.x = basePosition.x + (shakeAxes.x ? shakeX : 0);
      obj.position.y = basePosition.y + (shakeAxes.y ? shakeY : 0);
      obj.position.z = basePosition.z + (shakeAxes.z ? shakeZ : 0);
      break;
    }
    case 'wiggle': {
      const wiggleAmount = effect.wiggleAmount || { x: 5, y: 5, z: 0 };
      const wiggleSpeed = effect.wiggleSpeed || { x: 2, y: 3, z: 0 };
      const wiggleType = effect.wiggleType || 'sine';
      const wigglePhase = effect.wigglePhase || { x: 0, y: 90, z: 0 };

      const time = newTime - effect.startTime;
      let offsetX = 0,
        offsetY = 0,
        offsetZ = 0;

      if (wiggleType === 'sine') {
        offsetX =
          wiggleAmount.x *
          Math.sin(time * wiggleSpeed.x + (wigglePhase.x * Math.PI) / 180);
        offsetY =
          wiggleAmount.y *
          Math.sin(time * wiggleSpeed.y + (wigglePhase.y * Math.PI) / 180);
        offsetZ =
          wiggleAmount.z *
          Math.sin(time * wiggleSpeed.z + (wigglePhase.z * Math.PI) / 180);
      } else if (wiggleType === 'noise') {
        offsetX = wiggleAmount.x * (Math.random() - 0.5) * 2;
        offsetY = wiggleAmount.y * (Math.random() - 0.5) * 2;
        offsetZ = wiggleAmount.z * (Math.random() - 0.5) * 2;
      }

      // Add wiggle to base position
      obj.position.x = basePosition.x + offsetX;
      obj.position.y = basePosition.y + offsetY;
      obj.position.z = basePosition.z + offsetZ;
      break;
    }
    case 'bounce': {
      const bounceHeight = effect.bounceHeight || 5;
      const bounceCount = effect.bounceCount || 3;
      const bounceDecay = effect.bounceDecay || 0.8;
      const rawAxis = effect.bounceAxis ?? 'y';

      // Support both string ("x"|"y"|"z") and axes object ({x:boolean,y:boolean,z:boolean})
      let axis = 'y';
      if (typeof rawAxis === 'string') {
        axis = rawAxis;
      } else if (typeof rawAxis === 'object' && rawAxis !== null) {
        if (rawAxis.x) axis = 'x';
        else if (rawAxis.y) axis = 'y';
        else if (rawAxis.z) axis = 'z';
      }

      // Produce an up-then-return profile per bounce with decay
      // progressWithinBounce goes 0 -> 1 repeatedly across bounceCount cycles
      const progressWithinBounce = (easedProgress * bounceCount) % 1;
      const bounceIndex = Math.floor(easedProgress * bounceCount);
      const decayFactor = Math.pow(bounceDecay, bounceIndex);
      // Triangle wave: up then back down to base (0 -> 1 -> 0)
      const triangle =
        progressWithinBounce <= 0.5
          ? progressWithinBounce * 2
          : 2 - progressWithinBounce * 2;
      const bounceValue = bounceHeight * decayFactor * triangle;

      // Add bounce to base position
      if (axis === 'x') obj.position.x = basePosition.x + bounceValue;
      else if (axis === 'y') obj.position.y = basePosition.y + bounceValue;
      else if (axis === 'z') obj.position.z = basePosition.z + bounceValue;
      break;
    }
    // case 'orbit': {
    //   const orbitRadius = effect.orbitRadius || 5;
    //   const orbitSpeed = effect.orbitSpeed || 90;
    //   const orbitAxis = effect.orbitAxis || 'z';
    //   const orbitCenter = effect.orbitCenter || { x: 0, y: 0, z: 0 };
    //   const orbitDirection = effect.orbitDirection || 'clockwise';

    //   const angle =
    //     (orbitDirection === 'clockwise' ? -1 : 1) *
    //     ((orbitSpeed * Math.PI) / 180) *
    //     (newTime - effect.startTime);

    //   let orbitX = 0,
    //     orbitY = 0,
    //     orbitZ = 0;
    //   if (orbitAxis === 'z') {
    //     orbitX = orbitRadius * Math.cos(angle);
    //     orbitY = orbitRadius * Math.sin(angle);
    //   } else if (orbitAxis === 'y') {
    //     orbitX = orbitRadius * Math.cos(angle);
    //     orbitZ = orbitRadius * Math.sin(angle);
    //   } else if (orbitAxis === 'x') {
    //     orbitY = orbitRadius * Math.cos(angle);
    //     orbitZ = orbitRadius * Math.sin(angle);
    //   }

    //   // Add orbit to base position
    //   obj.position.x = basePosition.x + orbitX;
    //   obj.position.y = basePosition.y + orbitY;
    //   obj.position.z = basePosition.z + orbitZ;
    //   break;
    // }

    case 'orbit': {
      const orbitRadius = effect.orbitRadius ?? 5;
      const orbitSpeed = effect.orbitSpeed ?? 90;
      const orbitAxis = effect.orbitAxis ?? 'z';
      const orbitDirection = effect.orbitDirection ?? 'clockwise';
      const orbitTilt = ((effect.orbitTilt ?? 0) * Math.PI) / 180;

      const orbitCenter = effect.orbitCenter ?? { x: 0, y: 0, z: 0 };

      const angle =
        (orbitDirection === 'clockwise' ? -1 : 1) *
        ((orbitSpeed * Math.PI) / 180) *
        (newTime - effect.startTime);

      let orbitX = 0,
        orbitY = 0,
        orbitZ = 0;

      // --- Base orbit ---
      if (orbitAxis === 'z') {
        orbitX = orbitRadius * Math.cos(angle);
        orbitY = orbitRadius * Math.sin(angle);

        // Tilt around X axis
        const y = orbitY * Math.cos(orbitTilt);
        const z = orbitY * Math.sin(orbitTilt);
        orbitY = y;
        orbitZ = z;
      } else if (orbitAxis === 'y') {
        orbitX = orbitRadius * Math.cos(angle);
        orbitZ = orbitRadius * Math.sin(angle);

        // Tilt around X axis
        const z = orbitZ * Math.cos(orbitTilt);
        const y = orbitZ * Math.sin(orbitTilt);
        orbitZ = z;
        orbitY = y;
      } else if (orbitAxis === 'x') {
        orbitY = orbitRadius * Math.cos(angle);
        orbitZ = orbitRadius * Math.sin(angle);

        // Tilt around Y axis
        const z = orbitZ * Math.cos(orbitTilt);
        const x = orbitZ * Math.sin(orbitTilt);
        orbitZ = z;
        orbitX = x;
      }

      // --- Orbit center in world space ---
      const centerX = basePosition.x + orbitCenter.x;
      const centerY = basePosition.y + orbitCenter.y;
      const centerZ = basePosition.z + orbitCenter.z;

      // --- Apply orbit ---
      obj.position.x = centerX + orbitX;
      obj.position.y = centerY + orbitY;
      obj.position.z = centerZ + orbitZ;

      break;
    }

    case 'spring': {
      const springTension = effect.springTension || 170;
      const springFriction = effect.springFriction || 26;
      const springMass = effect.springMass || 1;
      const springRestPosition = effect.springRestPosition || {
        x: 0,
        y: 0,
        z: 0,
      };

      const damping =
        springFriction / (2 * Math.sqrt(springMass * springTension));
      const frequency = Math.sqrt(springTension / springMass) / (2 * Math.PI);

      if (damping < 1) {
        const dampedFreq = frequency * Math.sqrt(1 - damping * damping);
        const amplitude = Math.exp(
          -damping * frequency * 2 * Math.PI * easedProgress
        );
        const springValue =
          amplitude * Math.cos(2 * Math.PI * dampedFreq * easedProgress);

        // Add spring to base position
        obj.position.x =
          basePosition.x +
          (springRestPosition.x - basePosition.x) * springValue;
        obj.position.y =
          basePosition.y +
          (springRestPosition.y - basePosition.y) * springValue;
        obj.position.z =
          basePosition.z +
          (springRestPosition.z - basePosition.z) * springValue;
      }
      break;
    }
    case 'scale': {
      applyScaleEffectToObject(obj, effect, easedProgress);
      break;
    }
    case 'rotate':
    case 'fade':
    case 'fadein':
    case 'fadeout':
    case 'pulse':
      applyAnimationEffect(obj, effect, easedProgress, newTime);
      break;
    case 'playModel':
      // Handle GLB animation playback
      applyPlayModelEffect(obj, effect, easedProgress, newTime);
      break;
  }
};

/**
 * Handles Play Model effect - controls GLB animation playback
 */
export const applyPlayModelEffect = (
  obj,
  effect,
  easedProgress,
  _newTime,
  isStatic = false
) => {
  if (!obj) return;

  const animatableObj = getAnimatableObject(obj);
  if (!animatableObj?.userData) return;

  const mixer = ensureGlbMixer(animatableObj);
  if (
    !mixer ||
    !animatableObj.userData.animationActions ||
    animatableObj.userData.animationActions.length === 0
  ) {
    return;
  }

  const clipIndex = effect.clipIndex !== undefined ? effect.clipIndex : 0;
  const clipName = effect.clipName || '';
  const playSpeed = effect.playSpeed !== undefined ? effect.playSpeed : 1.0;

  let targetAction = null;
  const clips = animatableObj.userData.animationClips || [];

  if (clipName && clipName.trim() !== '') {
    const clipIndexByName = clips.findIndex((clip) => clip.name === clipName);
    if (
      clipIndexByName >= 0 &&
      clipIndexByName < animatableObj.userData.animationActions.length
    ) {
      targetAction = animatableObj.userData.animationActions[clipIndexByName];
    }
  }

  if (
    !targetAction &&
    clipIndex >= 0 &&
    clipIndex < animatableObj.userData.animationActions.length
  ) {
    targetAction = animatableObj.userData.animationActions[clipIndex];
  }

  if (!targetAction) {
    if (animatableObj.userData.animationActions.length > 0) {
      targetAction = animatableObj.userData.animationActions[0];
    } else {
      return;
    }
  }

  animatableObj.userData.animationActions.forEach((action) => {
    if (action !== targetAction) {
      action.stop();
      action.reset();
    }
  });

  const effectivePlaySpeed = playSpeed || 1;
  // Do NOT default easedProgress to 0 — undefined means continuous GLB playback
  const shouldScrub =
    isStatic || (easedProgress !== undefined && easedProgress !== null);

  targetAction.enabled = true;
  if (targetAction.setEffectiveWeight) {
    targetAction.setEffectiveWeight(1);
  } else {
    targetAction.weight = 1;
  }

  const sameEffect =
    animatableObj.userData.activePlayModelEffect === effect.id &&
    animatableObj.userData.previousClipActionIndex ===
      animatableObj.userData.animationActions.indexOf(targetAction);

  if (!targetAction.isRunning()) {
    targetAction.reset();
    targetAction.play();
  } else if (!sameEffect) {
    targetAction.reset();
    targetAction.play();
  }

  if (shouldScrub) {
    const clipDuration = targetAction.getClip().duration || 0;
    const progress = Math.min(1, Math.max(0, Number(easedProgress) || 0));
    let animationTime;
    if (effect.loop) {
      animationTime =
        clipDuration > 0
          ? (progress * clipDuration * effectivePlaySpeed) % clipDuration
          : 0;
    } else {
      animationTime = Math.min(
        progress * clipDuration * effectivePlaySpeed,
        clipDuration
      );
    }
    targetAction.paused = true;
    targetAction.time = Math.max(0, animationTime);
    mixer.update(0);
    animatableObj.updateMatrixWorld(true);
    setUserDataField(animatableObj, 'isGlbPlaying', false);
    setUserDataField(obj, 'isGlbPlaying', false);
  } else {
    // Continuous playback: render loop calls mixer.update(delta)
    targetAction.paused = false;
    if (targetAction.setEffectiveTimeScale) {
      targetAction.setEffectiveTimeScale(effectivePlaySpeed);
    } else if (targetAction.timeScale !== undefined) {
      targetAction.timeScale = effectivePlaySpeed;
    }
    setUserDataField(animatableObj, 'isGlbPlaying', true);
    setUserDataField(obj, 'isGlbPlaying', true);
  }

  setUserDataField(animatableObj, 'activePlayModelEffect', effect.id);
  setUserDataField(
    animatableObj,
    'previousClipActionIndex',
    animatableObj.userData.animationActions.indexOf(targetAction)
  );
  setUserDataField(obj, 'activePlayModelEffect', effect.id);
};

/**
 * Stops all playModel animations for a given object
 */
export const stopPlayModelForObject = (obj) => {
  if (!obj) return;

  const stopOnNode = (node) => {
    if (!node?.userData?.mixer || !node.userData.animationActions) return;
    const mixer = node.userData.mixer;
    node.userData.animationActions.forEach((action) => {
      if (action.isRunning()) {
        action.stop();
        action.reset();
      }
    });
    mixer.setTime(0);
    mixer.update(0);
    node.updateMatrixWorld(true);
    setUserDataField(node, 'isGlbPlaying', false);
    setUserDataField(node, 'activePlayModelEffect', null);
    setUserDataField(node, 'previousClipActionIndex', -1);
  };

  stopOnNode(obj);
  obj.traverse((child) => {
    if (child !== obj) stopOnNode(child);
  });

  setUserDataField(obj, 'isGlbPlaying', false);
  setUserDataField(obj, 'activePlayModelEffect', null);
};

export const applyAnimationEffect = (obj, effect, easedProgress, newTime) => {
  switch (effect.effectType) {
    case 'move':
      // Move effects are now handled in the first pass
      break;
    case 'rotate': {
      // Support both old and new property naming conventions
      const startRot = effect.startRotation || { x: 0, y: 0, z: 0 };
      const endRot = effect.endRotation || { x: 0, y: 0, z: 360 };

      const activeAxes =
        effect.activeAxes ||
        effect.rotationAxis || { x: false, y: false, z: true };

      const rotationDirection = effect.rotationDirection || 'clockwise';
      const pivotPoint = effect.pivotPoint || { x: 0, y: 0, z: 0 };

      const degToRad = Math.PI / 180;
      const direction = rotationDirection === 'clockwise' ? -1 : 1;

      // Store previous rotation on object (FIRST TIME SAFE).
      // History restore may leave plain {x,y,z} without .set — recreate Euler.
      if (typeof obj.userData.prevRotation?.set !== 'function') {
        obj.userData.prevRotation = new Three.Euler(
          obj.rotation.x,
          obj.rotation.y,
          obj.rotation.z
        );
      }

      // === CALCULATE TARGET ROTATION (ABSOLUTE) ===
      const targetRotX = activeAxes.x
        ? (startRot.x + (endRot.x - startRot.x) * easedProgress) *
        degToRad *
        direction
        : obj.rotation.x;

      const targetRotY = activeAxes.y
        ? (startRot.y + (endRot.y - startRot.y) * easedProgress) *
        degToRad *
        direction
        : obj.rotation.y;

      const targetRotZ = activeAxes.z
        ? (startRot.z + (endRot.z - startRot.z) * easedProgress) *
        degToRad *
        direction
        : obj.rotation.z;

      // === DELTA ROTATION (THIS IS THE FIX) ===
      const deltaX = targetRotX - obj.userData.prevRotation.x;
      const deltaY = targetRotY - obj.userData.prevRotation.y;
      const deltaZ = targetRotZ - obj.userData.prevRotation.z;

      // Apply rotation to object
      obj.rotation.set(targetRotX, targetRotY, targetRotZ);

      // === HANDLE PIVOT ROTATION (DELTA BASED) ===
      if (pivotPoint.x !== 0 || pivotPoint.y !== 0 || pivotPoint.z !== 0) {
        const pivot = new Three.Vector3(
          pivotPoint.x,
          pivotPoint.y,
          pivotPoint.z
        );

        // Move relative to pivot
        obj.position.sub(pivot);

        // Rotate POSITION by DELTA
        if (activeAxes.x && deltaX !== 0) {
          obj.position.applyAxisAngle(
            new Three.Vector3(1, 0, 0),
            deltaX
          );
        }

        if (activeAxes.y && deltaY !== 0) {
          obj.position.applyAxisAngle(
            new Three.Vector3(0, 1, 0),
            deltaY
          );
        }

        if (activeAxes.z && deltaZ !== 0) {
          obj.position.applyAxisAngle(
            new Three.Vector3(0, 0, 1),
            deltaZ
          );
        }

        // Move back
        obj.position.add(pivot);
      }

      // Save rotation for next frame
      obj.userData.prevRotation.set(
        targetRotX,
        targetRotY,
        targetRotZ
      );

      break;
    }
    case 'scale': {
      applyScaleEffectToObject(obj, effect, easedProgress);
      break;
    }
    case 'fade':

    // case 'fadein': {
    //   if (obj.material) {
    //     obj.material.transparent = true;
    //     obj.material.opacity = easedProgress;
    //     obj.material.needsUpdate = true;
    //   }

    //   // Always traverse children to handle complex objects like terrain with rocks
    //   obj.traverse((child) => {
    //     if (child.material) {
    //       if (Array.isArray(child.material)) {
    //         child.material.forEach((material) => {
    //           material.transparent = true;
    //           material.opacity = easedProgress;
    //           material.needsUpdate = true;
    //         });
    //       } else {
    //         child.material.transparent = true;
    //         child.material.opacity = easedProgress;
    //         child.material.needsUpdate = true;
    //       }
    //     }
    //   });
    //   break;
    // }

    case 'fadein': {
      const startOpacity =
        effect.startOpacity !== undefined ? effect.startOpacity : 0;

      // Default endOpacity to 1.0 or the object's configured opacity
      const configuredOp = getParticleConfiguredOpacity(obj);
      const endOpacity =
        effect.endOpacity !== undefined
          ? effect.endOpacity
          : configuredOp !== undefined
            ? configuredOp
            : 1;

      const currentOpacity =
        startOpacity + (endOpacity - startOpacity) * easedProgress;

      if (obj.material) {
        obj.material.transparent = true;
        obj.material.opacity = currentOpacity;
        obj.material.needsUpdate = true;

        // Handle water shader alpha uniform
        if (obj.material.uniforms && obj.material.uniforms.alpha) {
          obj.material.uniforms.alpha.value = currentOpacity;
        }
      }

      // Always traverse children to handle complex objects like terrain with rocks
      obj.traverse((child) => {
        if (child.material) {
          const materials = Array.isArray(child.material)
            ? child.material
            : [child.material];

          materials.forEach((material) => {
            material.transparent = true;
            material.opacity = currentOpacity;
            material.needsUpdate = true;

            // Handle water shader alpha uniform in children
            if (material.uniforms && material.uniforms.alpha) {
              material.uniforms.alpha.value = currentOpacity;
            }
          });
        }
      });

      break;
    }

    // case 'fadeout': {
    //   console.log(obj.file_name, 'obj');
    //   if (obj.material) {
    //     obj.material.transparent = true;
    //     obj.material.opacity = 1 - easedProgress;
    //     obj.material.needsUpdate = true;
    //   }

    //   // Always traverse children to handle complex objects like terrain with rocks
    //   obj.traverse((child) => {
    //     if (child.material) {
    //       if (Array.isArray(child.material)) {
    //         child.material.forEach((material) => {
    //           material.transparent = true;
    //           material.opacity = 1 - easedProgress;
    //           material.needsUpdate = true;
    //         });
    //       } else {
    //         child.material.transparent = true;
    //         child.material.opacity = 1 - easedProgress;
    //         child.material.needsUpdate = true;
    //       }
    //     }
    //   });
    //   break;
    // }

    case 'fadeout': {
      // Default startOpacity to 1.0 or the object's configured opacity
      const configuredOp = getParticleConfiguredOpacity(obj);
      const startOpacity =
        effect.startOpacity !== undefined
          ? effect.startOpacity
          : configuredOp !== undefined
            ? configuredOp
            : 1;

      const endOpacity =
        effect.endOpacity !== undefined ? effect.endOpacity : 0;

      const currentOpacity =
        startOpacity + (endOpacity - startOpacity) * easedProgress;

      if (obj.material) {
        obj.material.transparent = true;
        obj.material.opacity = currentOpacity;
        obj.material.needsUpdate = true;

        // Handle water shader alpha uniform
        if (obj.material.uniforms && obj.material.uniforms.alpha) {
          obj.material.uniforms.alpha.value = currentOpacity;
        }
      }

      // Always traverse children to handle complex objects like terrain with rocks
      obj.traverse((child) => {
        if (child.material) {
          const materials = Array.isArray(child.material)
            ? child.material
            : [child.material];

          materials.forEach((material) => {
            material.transparent = true;
            material.opacity = currentOpacity;
            material.needsUpdate = true;

            // Handle water shader alpha uniform in children
            if (material.uniforms && material.uniforms.alpha) {
              material.uniforms.alpha.value = currentOpacity;
            }
          });
        }
      });

      break;
    }

    case 'pulse': {
      const pulseScale = effect.pulseScale || 1.5;
      const pulseRate = effect.pulseRate || effect.pulseSpeed || 2;
      const pulseCount = effect.pulseCount || 1;
      const pulseSmooth =
        effect.pulseSmooth !== undefined ? effect.pulseSmooth : true;
      const pulseAxis = effect.pulseAxis || 'uniform';

      // Calculate pulse progress with count consideration
      const pulseProgress = Math.sin(
        easedProgress * Math.PI * 1 * pulseRate * pulseCount
      );
      const scaleFactor = pulseSmooth
        ? 1 + (pulseScale - 1) * (pulseProgress * 0.5 + 0.5)
        : 1 + (pulseScale - 1) * Math.abs(pulseProgress);

      // Handle axis configuration - support both object and string formats
      applyPulseScaleToObject(obj, scaleFactor, pulseAxis);
      break;
    }
    default:
      console.warn(`Unknown animation effect type: ${effect.effectType}`);
  }
};
